import { useChantierMetrics } from "@/hooks/useChantierMetrics";
import ProjectCard from "./ProjectCard";
import { JourDetail } from "@/types/calendar";

interface RealtimeProjectCardProps {
  project: any;
  hasAiAnalysis?: boolean;
  hasDelayRegistered?: boolean;
  dateDebutRetard?: string;
  dateFinRetard?: string;
  joursRetard?: number; // Nombre de jours ouvrés de retard (calculé correctement avec demi-journées)
  date_fin_initiale?: string; // Date fin AVANT ajout de retard (pour afficher période initiale)
  dateFinEffective?: string; // Date fin max entre date_fin_estimee et affectations
  heuresParJour?: number; // Heures par jour depuis première affectation (pour calcul progression)
  joursDetails?: JourDetail[]; // Tous les jours_details agrégés (pour calcul progression)
  suspensionDate?: string; // Date de suspension (si suspendu)
  suspensionRepriseEstimee?: string; // Date de reprise estimée (si définie)
  dateReprise?: string; // Date de reprise effective (pour afficher badge "Repris" 48h)
  onEdit: (id: string) => void;
  onDelete: (id: string) => Promise<void>;
  onChangeState?: (id: string, newState: string) => void;
  onArchive?: (id: string) => void;
  onUnarchive?: (id: string) => void;
  onCloturer?: (id: string) => void;
  onDemarrerBrouillon?: (id: string) => void;
  onProlongerRetard?: (id: string) => void;
}

const RealtimeProjectCard = ({ project, hasAiAnalysis, hasDelayRegistered, dateDebutRetard, dateFinRetard, joursRetard, date_fin_initiale, dateFinEffective, heuresParJour, joursDetails, suspensionDate, suspensionRepriseEstimee, dateReprise, onEdit, onDelete, onChangeState, onArchive, onUnarchive, onCloturer, onDemarrerBrouillon, onProlongerRetard }: RealtimeProjectCardProps) => {
  const { metrics, loading } = useChantierMetrics(project.id);

  const rentabilite = metrics?.marge_finale_pct || 0;
  const joursRestants = metrics?.jours_restants_rentables ?? project.duree_estimee_jours;
  const budgetDevis = metrics?.budget_ht || 0;
  const coutsEngages = (metrics?.couts_fixes_engages || 0) + (metrics?.cout_main_oeuvre_reel || 0);

  // Récupérer progress_pct depuis les métriques (calculé côté serveur)
  const progressPct = metrics?.progress_pct;

  // Utiliser jours_retard depuis les métriques serveur (source unique de vérité)
  // car le calcul côté client (joursRetard prop) peut être incorrect (somme par membre au lieu de jours uniques)
  const joursRetardServeur = metrics?.jours_retard ?? joursRetard;

  return (
    <ProjectCard
      key={project.id}
      id={project.id}
      nom_chantier={project.nom_chantier}
      client={project.client}
      rentabilite={rentabilite}
      jours_restants={joursRestants}
      budget_devis={budgetDevis}
      couts_engages={coutsEngages}
      etat_chantier={project.etat_chantier}
      statut={project.statut}
      date_debut_prevue={project.date_debut_prevue}
      date_fin_estimee={dateFinEffective || project.date_fin_estimee}
      duree_estimee_jours={metrics?.duree_estimee_jours || project.duree_estimee_jours}
      jours_details={joursDetails}
      heures_par_jour={heuresParJour}
      progress_pct={progressPct}
      pictogramme={project.pictogramme}
      couleur={project.couleur}
      hasAiAnalysis={hasAiAnalysis}
      hasDelayRegistered={hasDelayRegistered}
      dateDebutRetard={dateDebutRetard}
      dateFinRetard={dateFinRetard}
      joursRetard={joursRetardServeur}
      date_fin_initiale={date_fin_initiale || project.date_fin_initiale}
      suspensionDate={suspensionDate}
      suspensionRepriseEstimee={suspensionRepriseEstimee}
      dateReprise={dateReprise}
      onEdit={onEdit}
      onDelete={onDelete}
      onChangeState={onChangeState}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      onCloturer={onCloturer}
      onDemarrerBrouillon={onDemarrerBrouillon}
      onProlongerRetard={onProlongerRetard}
    />
  );
};

export default RealtimeProjectCard;
